import React, { useCallback, useEffect, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import { YEAR2_SEAL_REQUIRED_OUTPUT_TYPES } from '../../data/year2Councils';

function formatOutputType(type) {
  return String(type || '').replace(/_/g, ' ');
}

function buildChecklistLabels() {
  const councilByType = {
    strategic_intelligence_brief: 1,
    leverage_point_brief: 2,
    startup_proposal: 3,
    startup_report: 3,
    startup_youtube_url: 3,
    policy_proposal: 4,
    crisis_amendment_scan: 5,
  };

  const items = YEAR2_SEAL_REQUIRED_OUTPUT_TYPES.map((type) => ({
    key: `${type}:${councilByType[type]}`,
    output_type: type,
    council_number: councilByType[type],
    label: formatOutputType(type),
  }));

  for (let n = 1; n <= 5; n += 1) {
    items.push({
      key: `session_summary:${n}`,
      output_type: 'session_summary',
      council_number: n,
      label: `Session summary (Council ${n})`,
    });
  }

  return items;
}

const CHECKLIST_LABELS = buildChecklistLabels();

function statusBadgeClass(status) {
  if (status === 'approved') return 'status-completed';
  if (status === 'submitted') return 'status-planned';
  return 'status-cancelled';
}

function statusLabel(status) {
  if (status === 'approved') return 'Approved';
  if (status === 'submitted') return 'Pending';
  return 'Missing';
}

export default function Year2PortfolioDetail({ cohortId, cohortName, schoolName, isPA, onBack, onError, onSuccess }) {
  const [squads, setSquads] = useState([]);
  const [portfolioBySquad, setPortfolioBySquad] = useState({});
  const [loading, setLoading] = useState(false);
  const [sealingId, setSealingId] = useState(null);

  const loadSquadsAndPortfolio = useCallback(async (cid) => {
    if (!cid) {
      setSquads([]);
      setPortfolioBySquad({});
      return;
    }
    setLoading(true);
    try {
      const rows = await mentorApi.getYear2Squads(cid);
      setSquads(rows);
      const statuses = await Promise.all(
        rows.map(async (squad) => {
          const status = await mentorApi.getYear2PortfolioStatus(squad.id);
          return [String(squad.id), status];
        })
      );
      setPortfolioBySquad(Object.fromEntries(statuses));
    } catch (err) {
      onError?.(err?.message || 'Failed to load portfolio status');
      setSquads([]);
      setPortfolioBySquad({});
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    if (cohortId) {
      loadSquadsAndPortfolio(cohortId);
    }
  }, [cohortId, loadSquadsAndPortfolio]);

  const handleSeal = async (squadId) => {
    if (!isPA) return;
    setSealingId(squadId);
    try {
      await mentorApi.sealYear2Portfolio(squadId);
      const refreshed = await mentorApi.getYear2PortfolioStatus(squadId);
      setPortfolioBySquad((prev) => ({ ...prev, [String(squadId)]: refreshed }));
      onSuccess?.('Portfolio sealed.');
    } catch (err) {
      onError?.(err?.message || 'Failed to seal portfolio');
    } finally {
      setSealingId(null);
    }
  };

  const renderChecklist = (portfolio) => {
    const rowsByKey = {};
    (portfolio?.checklist || []).forEach((row) => {
      rowsByKey[`${row.output_type}:${row.council_number}`] = row;
    });

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
        {CHECKLIST_LABELS.map((item) => {
          const row = rowsByKey[item.key] || { status: 'missing' };
          return (
            <div
              key={item.key}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '10px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '13px' }}>{item.label}</span>
              <span className={`status-badge ${statusBadgeClass(row.status)}`} style={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                {statusLabel(row.status)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <button type="button" className="btn-form-toggle-outlined" onClick={onBack} style={{ marginBottom: '16px' }}>
        ← Back to cohorts
      </button>

      <div className="year2-page-header" style={{ marginBottom: '24px' }}>
        <h2 className="year2-page-title">{schoolName} · {cohortName}</h2>
        <p className="year2-page-subtitle">
          Review the 12 required outputs per squad before applying the portfolio seal.
        </p>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading portfolio status…</p>
      ) : squads.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No squads configured for this cohort.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
          {squads.map((squad) => {
            const portfolio = portfolioBySquad[String(squad.id)] || null;
            const isSealed = Boolean(portfolio?.seal);
            const canSeal = isPA && portfolio?.seal_eligible && !isSealed;

            return (
              <div key={squad.id} className="wizard-card" style={{ padding: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px' }}>{squad.name}</h3>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>{squad.topic}</p>
                  </div>
                  {isSealed && (
                    <span className="status-badge status-completed" style={{ textTransform: 'none' }}>
                      Portfolio Sealed
                    </span>
                  )}
                </div>

                {isSealed && portfolio.seal?.sealed_at && (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                    Sealed {new Date(portfolio.seal.sealed_at).toLocaleString()}
                  </p>
                )}

                {portfolio ? renderChecklist(portfolio) : (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Portfolio status unavailable.</p>
                )}

                {canSeal && (
                  <button
                    type="button"
                    className="btn-primary-action"
                    style={{ marginTop: '14px' }}
                    disabled={sealingId === squad.id}
                    onClick={() => handleSeal(squad.id)}
                  >
                    {sealingId === squad.id ? 'Sealing…' : 'Apply Portfolio Seal'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
